import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import {
  AUTOMATION_ACTIONS,
  AUTOMATION_RECIPIENTS,
  AUTOMATION_TRIGGER_MODELS,
  createEmptyWorkflow,
  getDynamicFieldsForTracker,
  getTriggersForModel,
  groupDynamicFields,
  MONTH_OPTIONS,
  RELATIVE_FROM_OPTIONS,
  RELATIVE_UNITS,
  saveWorkflowOnAutomationViaApi,
  toDynamicToken,
} from "./automationStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 700,
      color: MUTED,
      mb: 0.75,
      textTransform: "uppercase",
      letterSpacing: 0.35,
    }}
  >
    {children}
  </Typography>
);

const TokenChip = ({ label, token, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={toDynamicToken(token)}
    sx={{
      border: 0,
      bgcolor: "#f1f5f9",
      borderRadius: "6px",
      px: 1,
      py: 0.4,
      fontSize: 12,
      fontWeight: 600,
      color: "#334155",
      cursor: "pointer",
      textAlign: "left",
      lineHeight: 1.3,
      "&:hover": {
        bgcolor: PRIMARY_SOFT,
        color: PRIMARY,
      },
    }}
  >
    {label}
  </Box>
);

/**
 * Flat inline workflow editor (no section cards / tip copy).
 */
export default function WorkflowEditorPanel({
  automation,
  workflowId,
  isNew,
  onSaved,
  onCancel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(createEmptyWorkflow);
  const [errors, setErrors] = useState({});
  const [insertTarget, setInsertTarget] = useState("message");
  const [tokenSearch, setTokenSearch] = useState("");
  const subjectRef = useRef(null);
  const messageRef = useRef(null);

  useEffect(() => {
    if (!automation) return;
    if (isNew) {
      setForm({
        ...createEmptyWorkflow(),
        action: "email",
        recipients: "person",
      });
      setErrors({});
      setTokenSearch("");
      return;
    }
    const existing = (automation.workflows || []).find(
      (item) => item.id === workflowId
    );
    setForm(existing ? { ...createEmptyWorkflow(), ...existing } : null);
    setErrors({});
    setTokenSearch("");
  }, [automation, workflowId, isNew]);

  const dynamicFields = useMemo(
    () => getDynamicFieldsForTracker(automation?.trackerId),
    [automation?.trackerId]
  );

  const filteredGroupedFields = useMemo(() => {
    const q = tokenSearch.trim().toLowerCase();
    const source = q
      ? dynamicFields.filter(
          (field) =>
            field.label?.toLowerCase().includes(q) ||
            field.token?.toLowerCase().includes(q)
        )
      : dynamicFields;
    return groupDynamicFields(source);
  }, [dynamicFields, tokenSearch]);

  const triggersForModel = useMemo(
    () => getTriggersForModel(form?.triggerModel || "event"),
    [form?.triggerModel]
  );

  const update = (patch) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      if (patch.triggerModel && patch.triggerModel !== prev.triggerModel) {
        const first = getTriggersForModel(patch.triggerModel)[0];
        next.triggerId = first?.id || prev.triggerId;
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
  };

  const insertToken = (token) => {
    const marker = toDynamicToken(token);
    const field = insertTarget === "subject" ? "subject" : "message";
    const el =
      insertTarget === "subject" ? subjectRef.current : messageRef.current;
    const current = form[field] || "";
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + marker + current.slice(end);
      update({ [field]: next });
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + marker.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      update({ [field]: `${current}${marker}` });
    }
  };

  const validate = () => {
    const next = {};
    if (!form?.name?.trim()) next.name = "Name is required";
    if (!form?.triggerId) next.triggerId = "Choose a trigger";
    if (!form?.action) next.action = "Choose an action";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!automation?.id || !form) return;
    if (!validate()) return;
    try {
      const saved = await saveWorkflowOnAutomationViaApi(automation.id, {
        ...form,
        name: form.name.trim(),
        description: (form.description || "").trim(),
        subject: (form.subject || "").trim(),
        message: (form.message || "").trim(),
      });
      const workflow =
        (saved.workflows || []).find((item) => item.id === form.id) ||
        (saved.workflows || []).find(
          (item) => item.name === form.name.trim()
        ) ||
        (saved.workflows || [])[0];
      enqueueSnackbar(isNew ? "Workflow created" : "Workflow saved", {
        variant: "success",
      });
      onSaved?.(saved, workflow);
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save workflow", {
        variant: "error",
      });
    }
  };

  if (!form) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 14, color: MUTED }}>
          Workflow not found.
        </Typography>
      </Box>
    );
  }

  const showMessage = form.action === "email" || form.action === "notify";
  const groupEntries = Object.entries(filteredGroupedFields);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexShrink: 0,
          bgcolor: "#fff",
          zIndex: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
          {isNew ? "Add workflow" : form.name || "Edit workflow"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onCancel ? (
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={onCancel}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className="small-contained-button"
            onClick={handleSave}
          >
            {isNew ? "Save workflow" : "Update"}
          </button>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div className="row">
          <div className="col-md-8 mb-3">
            <FieldLabel>Name</FieldLabel>
            <TextField
              fullWidth
              size="small"
              required
              placeholder="Workflow name"
              value={form.name || ""}
              onChange={(e) => update({ name: e.target.value })}
              error={Boolean(errors.name)}
              helperText={errors.name || " "}
              sx={fieldSx}
            />
          </div>
          <div className="col-md-4 mb-3">
            <FieldLabel>Status</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.status || "active"}
              onChange={(e) => update({ status: e.target.value })}
              helperText=" "
              sx={fieldSx}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </TextField>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <FieldLabel>Trigger model</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.triggerModel || "event"}
              onChange={(e) => update({ triggerModel: e.target.value })}
              sx={fieldSx}
            >
              {AUTOMATION_TRIGGER_MODELS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.title}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-6 mb-3">
            <FieldLabel>Trigger</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.triggerId || ""}
              onChange={(e) => update({ triggerId: e.target.value })}
              error={Boolean(errors.triggerId)}
              helperText={errors.triggerId || " "}
              sx={fieldSx}
            >
              {triggersForModel.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>

          {form.triggerModel === "relative" ? (
            <>
              <div className="col-md-4 mb-3">
                <FieldLabel>After</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.relativeAmount ?? 7}
                  onChange={(e) =>
                    update({ relativeAmount: Number(e.target.value) || 0 })
                  }
                  sx={fieldSx}
                />
              </div>
              <div className="col-md-4 mb-3">
                <FieldLabel>Unit</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.relativeUnit || "days"}
                  onChange={(e) => update({ relativeUnit: e.target.value })}
                  sx={fieldSx}
                >
                  {RELATIVE_UNITS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="col-md-4 mb-3">
                <FieldLabel>From</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.relativeFrom || "journey_start"}
                  onChange={(e) => update({ relativeFrom: e.target.value })}
                  sx={fieldSx}
                >
                  {RELATIVE_FROM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </>
          ) : null}

          {form.triggerModel === "calendar" ? (
            <>
              <div className="col-md-6 mb-3">
                <FieldLabel>Month</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.calendarMonth || 1}
                  onChange={(e) =>
                    update({ calendarMonth: Number(e.target.value) })
                  }
                  sx={fieldSx}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="col-md-6 mb-3">
                <FieldLabel>Day</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  value={form.calendarDay ?? 1}
                  onChange={(e) =>
                    update({ calendarDay: Number(e.target.value) || 1 })
                  }
                  sx={fieldSx}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <FieldLabel>Action</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.action || "email"}
              onChange={(e) => update({ action: e.target.value })}
              error={Boolean(errors.action)}
              helperText={errors.action || undefined}
              sx={fieldSx}
            >
              {AUTOMATION_ACTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-6 mb-3">
            <FieldLabel>Recipients</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.recipients || "person"}
              onChange={(e) => update({ recipients: e.target.value })}
              sx={fieldSx}
            >
              {AUTOMATION_RECIPIENTS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {form.action === "assign_status" ? (
            <div className="col-md-6 mb-3">
              <FieldLabel>Status to assign</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={form.assignStatusName || ""}
                onChange={(e) => update({ assignStatusName: e.target.value })}
                placeholder="Must match a status on this tracker"
                sx={fieldSx}
              />
            </div>
          ) : null}
        </div>

        {showMessage ? (
          <>
            <div className="row">
              <div className="col-12 mb-3">
                <FieldLabel>Subject</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={form.subject || ""}
                  onChange={(e) => update({ subject: e.target.value })}
                  inputRef={subjectRef}
                  onFocus={() => setInsertTarget("subject")}
                  sx={fieldSx}
                />
              </div>
              <div className="col-12 mb-3">
                <FieldLabel>Body</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={6}
                  value={form.message || ""}
                  onChange={(e) => update({ message: e.target.value })}
                  inputRef={messageRef}
                  onFocus={() => setInsertTarget("message")}
                  sx={fieldSx}
                />
              </div>
              <div className="col-12 mb-1">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <FieldLabel>Insert field</FieldLabel>
                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    {["subject", "message"].map((target) => (
                      <Box
                        key={target}
                        component="button"
                        type="button"
                        onClick={() => {
                          setInsertTarget(target);
                          if (target === "subject") subjectRef.current?.focus();
                          else messageRef.current?.focus();
                        }}
                        sx={{
                          border: 0,
                          bgcolor:
                            insertTarget === target ? PRIMARY_SOFT : "#f1f5f9",
                          color: insertTarget === target ? PRIMARY : MUTED,
                          borderRadius: "6px",
                          px: 1,
                          py: 0.35,
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "capitalize",
                        }}
                      >
                        {target}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search fields…"
                  value={tokenSearch}
                  onChange={(e) => setTokenSearch(e.target.value)}
                  sx={{ ...fieldSx, mb: 1.25 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.65,
                    maxHeight: 160,
                    overflow: "auto",
                  }}
                >
                  {groupEntries.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                      No fields match.
                    </Typography>
                  ) : (
                    groupEntries.flatMap(([, fields]) =>
                      fields.map((field) => (
                        <TokenChip
                          key={field.token}
                          label={field.label}
                          token={field.token}
                          onClick={() => insertToken(field.token)}
                        />
                      ))
                    )
                  )}
                </Box>
              </div>
            </div>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
